import { useState, useCallback } from 'react';
import {
  X, Upload, FileSpreadsheet, ArrowLeft, AlertTriangle, Info,
  Check,
} from 'lucide-react';
import SpecImportGuideTab from './SpecImportGuideTab';
import toast from 'react-hot-toast';
import {
  useSpecImportPreviewJSON,
  useSpecImportPreviewXLSX,
  useSpecImportConfirm,
} from '../../hooks/useSpecifications';
import RequirementTreeSelect from '../requirement/RequirementTreeSelect';
import type {
  SpecImportPreviewResult,
  SpecImportPreviewItem,
  SpecImportConflictItem,
} from '../../services/specificationService';
import Spinner from '../ui/Spinner';

const STATUS_LABEL: Record<string, string> = {
  DRAFT: '초안',
  IN_REVIEW: '검토 중',
  APPROVED: '승인됨',
  IMPLEMENTED: '구현됨',
};

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  IN_REVIEW: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  IMPLEMENTED: 'bg-blue-100 text-blue-800',
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

interface Props {
  projectId: string;
  onClose: () => void;
  onImported: () => void;
}

export default function SpecImportModal({ projectId, onClose, onImported }: Props) {
  const [activeTab, setActiveTab] = useState<'upload' | 'guide'>('upload');
  const [step, setStep] = useState<'input' | 'preview'>('input');

  // input step 상태
  const [inputMode, setInputMode] = useState<'json' | 'file'>('json');
  const [jsonText, setJsonText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [requirementId, setRequirementId] = useState('');

  // preview step 상태
  const [previewResult, setPreviewResult] = useState<SpecImportPreviewResult | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [conflictResolutions, setConflictResolutions] = useState<Record<number, string>>({});

  // Mutations
  const previewJsonMutation = useSpecImportPreviewJSON(projectId);
  const previewXlsxMutation = useSpecImportPreviewXLSX(projectId);
  const confirmMutation = useSpecImportConfirm(projectId);

  const isPreviewing = previewJsonMutation.isPending || previewXlsxMutation.isPending;

  // --- 파일 선택 ---
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();

    if (name.endsWith('.json')) {
      setFile(null);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setJsonText(ev.target?.result as string || '');
        setInputMode('json');
      };
      reader.readAsText(f);
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      setFile(f);
      setInputMode('file');
    } else {
      toast.error('.json 또는 .xlsx 파일을 선택해주세요.');
    }
    e.target.value = '';
  }, []);

  // --- 미리보기 ---
  const handlePreview = useCallback(async () => {
    if (!requirementId) {
      toast.error('대상 요구사항을 선택해주세요.');
      return;
    }

    try {
      let result: SpecImportPreviewResult;

      if (inputMode === 'file' && file) {
        result = await previewXlsxMutation.mutateAsync({ file, requirementId });
      } else if (jsonText.trim()) {
        let parsed: unknown;
        try {
          parsed = JSON.parse(jsonText);
        } catch {
          toast.error('JSON 형식이 올바르지 않습니다.');
          return;
        }
        result = await previewJsonMutation.mutateAsync({ data: parsed, requirementId });
      } else {
        toast.error('JSON을 입력하거나 파일을 선택해주세요.');
        return;
      }

      setPreviewResult(result);
      // 유효 + 경고 항목 모두 기본 선택
      const allSelectable = [...result.valid, ...result.warnings];
      setSelectedItems(new Set(allSelectable.map((i) => i.index)));
      // 충돌 항목 기본값: overwrite
      const defaultRes: Record<number, string> = {};
      result.conflicts.forEach((c) => { defaultRes[c.index] = 'overwrite'; });
      setConflictResolutions(defaultRes);
      setStep('preview');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '미리보기 처리 중 오류가 발생했습니다.');
    }
  }, [inputMode, file, jsonText, requirementId, previewJsonMutation, previewXlsxMutation]);

  // --- Import 확정 ---
  const handleConfirm = useCallback(async () => {
    if (!previewResult || selectedItems.size === 0) return;

    const allItems = [...previewResult.valid, ...previewResult.warnings];
    const selectedList = allItems
      .filter((item) => selectedItems.has(item.index))
      .map((item) => ({
        index: item.index,
        mapped: item.mapped,
        conflictResolution: conflictResolutions[item.index] as 'overwrite' | 'skip' | 'create_new' | undefined,
      }));

    try {
      const res = await confirmMutation.mutateAsync({
        items: selectedList,
        requirementId,
      });
      toast.success(res.message);
      onImported();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Import에 실패했습니다.');
    }
  }, [previewResult, selectedItems, conflictResolutions, requirementId, confirmMutation, onImported, onClose]);

  const handleBack = useCallback(() => {
    setStep('input');
    setPreviewResult(null);
    setSelectedItems(new Set());
    setConflictResolutions({});
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col mx-4">
        {/* 헤더 + 탭 */}
        <div className="px-6 pt-5 pb-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">기능명세 Import</h3>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'upload'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              JSON/XLSX 업로드
            </button>
            <button
              onClick={() => setActiveTab('guide')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'guide'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              마크다운에서 변환 가이드
            </button>
          </div>
        </div>

        {/* 탭 내용 */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'upload' && step === 'input' && (
            <InputStep
              inputMode={inputMode}
              setInputMode={setInputMode}
              jsonText={jsonText}
              setJsonText={setJsonText}
              file={file}
              setFile={setFile}
              requirementId={requirementId}
              setRequirementId={setRequirementId}
              projectId={projectId}
              onFileChange={handleFileChange}
            />
          )}
          {activeTab === 'upload' && step === 'preview' && previewResult && (
            <PreviewStep
              result={previewResult}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              conflictResolutions={conflictResolutions}
              setConflictResolutions={setConflictResolutions}
            />
          )}
          {activeTab === 'guide' && <GuideTab />}
        </div>

        {/* 푸터 */}
        {activeTab === 'upload' && (
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            {step === 'input' ? (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handlePreview}
                  disabled={isPreviewing || (!jsonText.trim() && !file) || !requirementId}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isPreviewing && <Spinner className="size-4 text-white" />}
                  미리보기
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft className="size-4" />
                  이전
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={confirmMutation.isPending || selectedItems.size === 0}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {confirmMutation.isPending && <Spinner className="size-4 text-white" />}
                  선택 항목 Import ({selectedItems.size}건)
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// Step 1: 입력
// ─────────────────────────────────────────────────────

interface InputStepProps {
  inputMode: 'json' | 'file';
  setInputMode: (v: 'json' | 'file') => void;
  jsonText: string;
  setJsonText: (v: string) => void;
  file: File | null;
  setFile: (f: File | null) => void;
  requirementId: string;
  setRequirementId: (v: string) => void;
  projectId: string;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function InputStep({
  inputMode, setInputMode, jsonText, setJsonText, file, setFile,
  requirementId, setRequirementId, projectId, onFileChange,
}: InputStepProps) {
  return (
    <div className="space-y-5">
      {/* 대상 요구사항 선택 (CHG-029: 트리 선택) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          대상 요구사항 <span className="text-red-500">*</span>
        </label>
        <RequirementTreeSelect
          projectId={projectId}
          value={requirementId || null}
          onChange={(id) => setRequirementId(id ?? '')}
          placeholder="요구사항을 선택하세요"
        />
        <p className="text-sm text-gray-500 mt-1">
          Import된 모든 명세가 선택한 요구사항 하위에 생성됩니다.
        </p>
      </div>

      {/* 입력 방식 선택 */}
      <div className="flex items-center gap-4">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={inputMode === 'json'}
            onChange={() => { setInputMode('json'); setFile(null); }}
            className="size-4 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">JSON 붙여넣기</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            checked={inputMode === 'file'}
            onChange={() => setInputMode('file')}
            className="size-4 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">파일 업로드 (JSON/XLSX)</span>
        </label>
      </div>

      {/* JSON 입력 영역 */}
      {inputMode === 'json' && (
        <textarea
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          placeholder={'[\n  {\n    "functionCode": "FN-001",\n    "title": "로그인",\n    "mainFlow": [\n      { "step": 1, "description": "접근한다" }\n    ]\n  }\n]'}
          rows={12}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono text-gray-800 placeholder-gray-400 resize-y focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />
      )}

      {/* 파일 업로드 영역 */}
      {inputMode === 'file' && (
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
            <Upload className="size-4" />
            파일 선택 (.json, .xlsx)
            <input
              type="file"
              accept=".json,.xlsx,.xls"
              onChange={onFileChange}
              className="hidden"
            />
          </label>
          {file ? (
            <div className="flex items-center gap-2 text-sm">
              <FileSpreadsheet className="size-4 text-green-600" />
              <span className="text-green-700 font-medium">
                {file.name} ({formatFileSize(file.size)})
              </span>
              <button onClick={() => setFile(null)} className="p-0.5 text-gray-400 hover:text-red-500">
                <X className="size-3.5" />
              </button>
            </div>
          ) : (
            <span className="text-sm text-gray-400">선택된 파일 없음</span>
          )}
        </div>
      )}

      {/* 안내 */}
      <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
        <Info className="size-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium mb-1">다양한 형식을 자동으로 인식합니다.</p>
          <ul className="space-y-0.5 text-blue-600">
            <li>JSON: 한국어/영어 키, camelCase/snake_case, 단일 객체 또는 배열</li>
            <li>XLSX: 컬럼 헤더 자동 매핑, 셀 내 줄바꿈으로 흐름 자동 분리</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// Step 2: 미리보기
// ─────────────────────────────────────────────────────

interface PreviewStepProps {
  result: SpecImportPreviewResult;
  selectedItems: Set<number>;
  setSelectedItems: React.Dispatch<React.SetStateAction<Set<number>>>;
  conflictResolutions: Record<number, string>;
  setConflictResolutions: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

function PreviewStep({
  result, selectedItems, setSelectedItems,
  conflictResolutions, setConflictResolutions,
}: PreviewStepProps) {
  const { valid, warnings, errors, conflicts, xlsxInfo } = result;

  const allSelectable = [...valid, ...warnings];

  const toggleSelect = (idx: number) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === allSelectable.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(allSelectable.map((i) => i.index)));
    }
  };

  const conflictSet = new Set(conflicts.map((c) => c.index));
  const conflictMap = new Map<number, SpecImportConflictItem>();
  conflicts.forEach((c) => conflictMap.set(c.index, c));

  return (
    <div className="space-y-4">
      {/* XLSX 정보 */}
      {xlsxInfo && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg text-sm text-green-700">
          <FileSpreadsheet className="size-4" />
          <span>
            <span className="font-medium">{xlsxInfo.sheetName}</span> 시트에서{' '}
            {xlsxInfo.totalRows}행 중 <span className="font-medium">{xlsxInfo.filteredRows}행</span> 추출
          </span>
        </div>
      )}

      {/* 요약 바 */}
      <div className="flex items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
          <Check className="size-4" />
          유효 {valid.length}건
        </span>
        {warnings.length > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
            <AlertTriangle className="size-4" />
            경고 {warnings.length}건
          </span>
        )}
        {errors.length > 0 && (
          <span className="inline-flex items-center gap-1 text-red-600 font-medium">
            <X className="size-4" />
            오류 {errors.length}건
          </span>
        )}
        {conflicts.length > 0 && (
          <span className="text-orange-600 font-medium">
            충돌 {conflicts.length}건
          </span>
        )}
      </div>

      {/* 유효 + 경고 항목 목록 */}
      {allSelectable.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <input
              type="checkbox"
              checked={selectedItems.size === allSelectable.length && allSelectable.length > 0}
              onChange={toggleSelectAll}
              className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              전체 선택 ({allSelectable.length})
            </span>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
            {allSelectable.map((item) => (
              <SpecPreviewRow
                key={item.index}
                item={item}
                checked={selectedItems.has(item.index)}
                onToggle={() => toggleSelect(item.index)}
                hasConflict={conflictSet.has(item.index)}
                conflict={conflictMap.get(item.index)}
                conflictResolution={conflictResolutions[item.index]}
                onConflictResolutionChange={(val) =>
                  setConflictResolutions((prev) => ({ ...prev, [item.index]: val }))
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* 오류 항목 */}
      {errors.length > 0 && (
        <div className="border border-red-200 rounded-lg p-3 bg-red-50">
          <p className="text-sm font-medium text-red-700 mb-1.5">
            오류 (Import 불가) — {errors.length}건
          </p>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {errors.map((item) => (
              <div key={item.index} className="text-sm text-red-600">
                <span className="font-mono">{item.index + 1}번째 항목:</span>{' '}
                <span>{item.errors.join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// 미리보기 행 컴포넌트
// ─────────────────────────────────────────────────────

interface SpecPreviewRowProps {
  item: SpecImportPreviewItem;
  checked: boolean;
  onToggle: () => void;
  hasConflict: boolean;
  conflict?: SpecImportConflictItem;
  conflictResolution?: string;
  onConflictResolutionChange: (val: string) => void;
}

function SpecPreviewRow({
  item, checked, onToggle, hasConflict, conflict,
  conflictResolution, onConflictResolutionChange,
}: SpecPreviewRowProps) {
  return (
    <div className={`px-4 py-2.5 ${item.warnings.length > 0 ? 'bg-amber-50/50' : ''}`}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="size-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-primary-600 font-medium">{item.functionCode}</span>
            <span className="text-sm text-gray-900 truncate">{item.title}</span>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${STATUS_COLOR[item.status] || 'bg-gray-100 text-gray-700'}`}>
              {STATUS_LABEL[item.status] || item.status}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-500">
            <span>주요흐름 {item.mainFlowStepCount}단계</span>
          </div>
          {/* 경고 메시지 */}
          {item.warnings.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {item.warnings.map((w, i) => (
                <p key={i} className="text-sm text-amber-600 flex items-start gap-1">
                  <AlertTriangle className="size-3 mt-0.5 shrink-0" />
                  <span>{w}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      </label>

      {/* 충돌 해결 옵션 */}
      {hasConflict && conflict && (
        <div className="ml-7 mt-2 p-2.5 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-700 font-medium mb-2">
            기존 명세와 충돌: &quot;{conflict.existingTitle}&quot;
          </p>
          <div className="flex flex-col gap-1.5">
            {(['overwrite', 'skip', 'create_new'] as const).map((opt) => (
              <label key={opt} className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name={`conflict-${item.index}`}
                  checked={conflictResolution === opt}
                  onChange={() => onConflictResolutionChange(opt)}
                  className="size-3.5 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  {opt === 'overwrite' && '덮어쓰기 (기존 명세 업데이트)'}
                  {opt === 'skip' && '건너뛰기 (Import에서 제외)'}
                  {opt === 'create_new' && '새로 생성 (코드 뒤에 -1 추가)'}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// 가이드 탭 — SpecImportGuideTab.tsx로 분리
// ─────────────────────────────────────────────────────

function GuideTab() {
  return <SpecImportGuideTab />;
}
